/**
 * Wizard creazione bozza FIR digitale — form guidato in-page.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { endpoints, type DraftItem } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/forms";
import { FirBozzaFormPanel } from "@/components/FirBozzaFormPanel";
import { useOperatoreContext } from "@/lib/operatoreContext";
import {
  buildFirBozzaPayload,
  defaultFirBozzaForm,
  type FirBozzaFormState,
} from "@/lib/firBozzaForm";

export function FirWizardDialog({
  open,
  onOpenChange,
  onCreated,
  stayOnPage = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (d: DraftItem) => void;
  /** Se true, non reindirizza a /drafts (es. da pagina Formulari). */
  stayOnPage?: boolean;
}) {
  const navigate = useNavigate();
  const ctx = useOperatoreContext();
  const sito = ctx.numIscrSito;
  const [title, setTitle] = useState("");
  const [form, setForm] = useState<FirBozzaFormState>(() => defaultFirBozzaForm(sito));

  useEffect(() => {
    if (!open) return;
    setForm(defaultFirBozzaForm(sito));
    setTitle("");
  }, [open, sito]);

  const create = useMutation({
    mutationFn: () => {
      if (!sito) throw new Error("Seleziona un'unità locale nella barra laterale");
      if (!form.eer) throw new Error("Seleziona un codice CER attivo");
      const payload = buildFirBozzaPayload({ ...form, num_iscr_sito: sito });
      return endpoints.drafts.create({
        doc_type: "fir",
        title: title.trim() || `FIR ${form.eer}`,
        payload,
      });
    },
    onSuccess: (d) => {
      toast.success("Bozza formulario creata");
      onCreated?.(d);
      onOpenChange(false);
      if (!stayOnPage) {
        navigate(`/drafts/${d.id}`);
      }
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore creazione bozza"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo formulario digitale</DialogTitle>
          <DialogDescription>
            Compila i dati del FIR per l&apos;unità locale{" "}
            <span className="font-mono">{sito || "—"}</span>. Potrai inviare a RENTRI da Bozze.
          </DialogDescription>
        </DialogHeader>
        {!sito ? (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Imposta CF e unità locale nella barra laterale.
          </p>
        ) : (
          <div className="space-y-4">
            <FormField label="Titolo bozza (opzionale)">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Es. Trasporto settimanale"
              />
            </FormField>
            <FirBozzaFormPanel form={form} onChange={setForm} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!sito || !form.eer || create.isPending}
          >
            {create.isPending ? "Creazione…" : "Crea bozza"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
